import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import data from "../../data/index.json";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { getBlogImage } from "../../constants/images";
import { HiArrowLeft, HiArrowRight, HiShare } from "react-icons/hi";

const ease = [0.22, 1, 0.36, 1];

const BlogDetails = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [readProgress, setReadProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setReadProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const blog = data.BlogApi.find((b) => b.id === parseInt(id));
  const allBlogs = data.BlogApi;
  const currentIndex = allBlogs.findIndex((b) => b.id === parseInt(id));
  const prevBlog = currentIndex > 0 ? allBlogs[currentIndex - 1] : null;
  const nextBlog = currentIndex < allBlogs.length - 1 ? allBlogs[currentIndex + 1] : null;

  const handleBackClick = () => {
    navigate("/blog", { state: { scrollPosition: location.state?.scrollPosition || 0 } });
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: blog?.title_one, url });
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-6">
        <div className="text-center">
          <span className="label">404</span>
          <h1 className="display mt-4">{t("Blog post not found")}</h1>
          <p className="lede mt-4 mx-auto">
            {t("The requested article could not be found.")}
          </p>
          <button type="button" onClick={handleBackClick} className="btn btn-solid mt-8">
            <HiArrowLeft aria-hidden /> {t("Back to Articles")}
          </button>
        </div>
      </div>
    );
  }

  const blogImage = getBlogImage(blog.image);

  const wordCount = [blog.desc_one, blog.desc_two, blog.desc_three]
    .filter(Boolean)
    .join(" ")
    .split(/\s+/).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  const sections = [
    blog.title_two && { title: blog.title_two, desc: blog.desc_two },
    blog.title_three && { title: blog.title_three, desc: blog.desc_three },
  ].filter(Boolean);

  const canonicalUrl = `https://hyphentech.africa/blog/${blog.id}`;
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://hyphentech.africa/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://hyphentech.africa/blog" },
      { "@type": "ListItem", position: 3, name: blog.title_one, item: canonicalUrl },
    ],
  };
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog.title_one,
    description: blog.desc_one,
    datePublished: blog.date,
    author: { "@type": "Organization", name: "Hyphen Tech" },
    url: canonicalUrl,
  };

  return (
    <div className="app min-h-screen bg-paper">
      <Helmet>
        <title>{`${blog.title_one} - Hyphen Tech Blog`}</title>
        <meta name="description" content={blog.desc_one} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Reading progress — a hairline at the very top of the page. */}
      <div
        aria-hidden
        className="fixed top-0 left-0 h-px bg-gold z-[200]"
        style={{ width: `${readProgress}%` }}
      />

      <div className="shell py-12 md:py-16 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={handleBackClick}
          className="link-underline text-sm font-medium"
        >
          <HiArrowLeft aria-hidden /> {t("Back to Articles")}
        </button>
        <button
          type="button"
          onClick={handleShare}
          className="link-underline text-sm font-medium"
        >
          <HiShare aria-hidden /> {t("Share")}
        </button>
      </div>

      <article>
        {/* ── Masthead ── */}
        <header className="shell pb-12 md:pb-16">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="max-w-[46rem] border-t border-[color:var(--rule)] pt-6"
          >
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="label tabular text-gold-ink">{blog.date}</span>
              <span className="label tabular">
                {readingTime} {t("min read")}
              </span>
            </div>

            <h1 className="display-hero mt-6">{t(blog.title_one)}</h1>
          </motion.div>
        </header>

        {/* ── Plate ── */}
        <motion.figure
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.1, ease }}
          className="shell"
        >
          <div className="overflow-hidden bg-paper-tint aspect-[16/9] md:aspect-[2/1]">
            <img
              src={blogImage}
              alt={t(blog.title_one)}
              className="w-full h-full object-cover"
            />
          </div>
          <figcaption>{t(blog.title_one)}</figcaption>
        </motion.figure>

        {/* ── Body ── */}
        <div className="shell py-14 md:py-20">
          <div className="max-w-[42rem]">
            <div className="prose-body text-lg has-dropcap">
              <p>{t(blog.desc_one)}</p>
            </div>

            {sections.map((section, i) => (
              <motion.section
                key={i}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, ease }}
                className="mt-12 pt-8 border-t border-[color:var(--rule)]"
              >
                <span className="label tabular text-gold-ink">
                  {String(i + 2).padStart(2, "0")}
                </span>
                <h2 className="display-2 mt-3">{t(section.title)}</h2>
                <p className="prose-body mt-4 text-lg">{t(section.desc)}</p>
              </motion.section>
            ))}

            <footer className="mt-14 pt-6 border-t border-[color:var(--rule)] flex flex-wrap items-baseline justify-between gap-3">
              <p className="label">{t("Published by")} Hyphen Tech</p>
              <p className="label tabular">
                {readingTime} {t("min")} · {wordCount} {t("words")}
              </p>
            </footer>
          </div>
        </div>
      </article>

      {/* ── Continue reading ── */}
      {(prevBlog || nextBlog) && (
        <nav aria-label={t("More articles")} className="shell pb-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-[color:var(--rule)]">
            {prevBlog ? (
              <button
                type="button"
                onClick={() => navigate(`/blog/${prevBlog.id}`)}
                className="group text-left py-8 sm:pr-8 border-b sm:border-b-0 sm:border-r border-[color:var(--rule)] transition-colors hover:bg-paper-warm"
              >
                <span className="label inline-flex items-center gap-2">
                  <HiArrowLeft aria-hidden className="transition-transform group-hover:-translate-x-1" />
                  {t("Previous")}
                </span>
                <p className="display-3 mt-3 line-clamp-2">{t(prevBlog.title_one)}</p>
              </button>
            ) : (
              <div className="hidden sm:block" />
            )}

            {nextBlog && (
              <button
                type="button"
                onClick={() => navigate(`/blog/${nextBlog.id}`)}
                className="group text-left sm:text-right py-8 sm:pl-8 transition-colors hover:bg-paper-warm"
              >
                <span className="label inline-flex items-center gap-2">
                  {t("Next Article")}
                  <HiArrowRight aria-hidden className="transition-transform group-hover:translate-x-1" />
                </span>
                <p className="display-3 mt-3 line-clamp-2">{t(nextBlog.title_one)}</p>
              </button>
            )}
          </div>

          <button type="button" onClick={handleBackClick} className="btn btn-solid mt-12">
            <HiArrowLeft aria-hidden /> {t("Back to all Articles")}
          </button>
        </nav>
      )}
    </div>
  );
};

export default BlogDetails;
